import { all, call, put, select, takeLatest } from "redux-saga/effects";

import { Route } from "@mapmycustomers/shared/types/entity";
import Organization from "@mapmycustomers/shared/types/Organization";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";
import ViewState from "@mapmycustomers/shared/types/viewModel/ViewState";

import localSettings from "@app/config/LocalSettings";
import { callApi } from "@app/store/api/callApi";
import { handleError } from "@app/store/errors/actions";
import { getOrganizationId } from "@app/store/iam";
import routeFieldModel, { RouteFieldName } from "@app/util/fieldModel/RouteFieldModel";
import { createEmptyFilterModel, extractSimpleConditionForField } from "@app/util/filters/utils";
import { isSimpleCondition } from "@app/util/viewModel/assert";
import { convertToPlatformSortModel } from "@app/util/viewModel/convertSort";
import { convertToPlatformFilterModel } from "@app/util/viewModel/convertToPlatformFilterModel";

import { applyListViewSettings, fetchRoutesList, initializeListView } from "./actions";
import { defaultViewState, LIST_VIEW_KEY } from "./reducer";
import { getListViewState } from "./selectors";

export function* onInitializeListView() {
  try {
    const persistedViewState: ViewState = localSettings.getViewSettings(
      LIST_VIEW_KEY,
      routeFieldModel,
      defaultViewState
    );
    yield put(initializeListView.success(persistedViewState));
  } catch (error) {
    yield put(initializeListView.failure(error));
    yield put(handleError({ error }));
  }
}

export function* onFetchRoutesList({ payload }: ReturnType<typeof fetchRoutesList.request>) {
  try {
    if (!payload.fetchOnlyWithoutFilters) {
      yield put(applyListViewSettings(payload.request));
    }

    const viewState: ViewState = yield select(getListViewState);

    if (!payload.fetchOnlyWithoutFilters) {
      localSettings.setViewSettings(viewState, LIST_VIEW_KEY);
    }

    if (payload.updateOnly) {
      return;
    }

    const $offset =
      payload.fetchOnlyWithoutFilters && payload.request.range
        ? payload.request.range.startRow
        : viewState.range.startRow;
    const $limit =
      payload.fetchOnlyWithoutFilters && payload.request.range
        ? payload.request.range.endRow - payload.request.range.startRow
        : viewState.range.endRow - viewState.range.startRow;

    const orgId: Organization["id"] = yield select(getOrganizationId);

    const [quickFilter, filter] = extractSimpleConditionForField(
      viewState.filter,
      RouteFieldName.QUICK_FILTER
    );

    const $filters: Record<string, unknown> = {
      ...convertToPlatformFilterModel(
        payload.fetchOnlyWithoutFilters ? createEmptyFilterModel() : filter,
        viewState.columns,
        routeFieldModel
      ),
      // the row menu gates Delete on route.accessStatus, which the API omits by default
      includeAccessStatus: true,
    };

    if (!payload.fetchOnlyWithoutFilters && quickFilter && isSimpleCondition(quickFilter)) {
      const { value } = quickFilter;
      if (!("$and" in $filters)) {
        $filters.$and = [];
      }
      ($filters.$and as unknown[]).push({ $or: [{ name: { $in: value } }] });
    }

    const response: ListResponse<Route> = yield callApi("fetchUnifiedRoutes", orgId, {
      $filters,
      $limit,
      $offset,
      $order: convertToPlatformSortModel(viewState.sort),
    });

    if (payload.dataCallback) {
      yield call(payload.dataCallback, response);
    }

    yield put(
      fetchRoutesList.success({
        totalFilteredRecords: response.total,
        totalRecords: response.accessible ?? response.total,
      })
    );
  } catch (error) {
    if (payload.failCallback) {
      yield call(payload.failCallback);
    }
    yield put(fetchRoutesList.failure(error));
    yield put(handleError({ error }));
  }
}

export function* listViewSaga() {
  yield all([
    takeLatest(initializeListView.request, onInitializeListView),
    takeLatest(fetchRoutesList.request, onFetchRoutesList),
  ]);
}
